import React, {PureComponent} from "react"
import {withRouter, RouteComponentProps} from "react-router-dom"
import qs from "qs"
import {get} from "lodash"
import PropTypes from "prop-types"

import History from "@irati/utils/history"
import id from "@irati/utils/id"
import {Body, H2} from "@irati/ui/src/Typography"
import Form, {FormGroup} from "@irati/ui/src/Form"
import Button from "@irati/ui/src/Button"
import {Input} from "@irati/ui/src/FormItem"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import {Password} from "@irati-web/fields/user"
import {Col} from "@irati/ui/src/Layout"
import Validation from "@irati/utils/validation"
import authentication from "@irati/auth"
import Loading from "@irati/ui/src/Loading"
import {FormattedMessage, InjectIntl, getMessage, injectIntl} from "@irati/utils/lang"
import loginService from "@irati/auth/services/login"
import Link, {RouterLink} from "@irati/ui/src/Link"
import {initiateSSOLogin} from "@irati/sso/helpers/handleSSOFlow"
import ssoErrorHandler from "@irati/sso/services/errors/ssoErrorHandler"
import ErrorText from "@irati/ui/src/ErrorText"
import Spacing from "@irati/ui/src/Spacing"
import {getAuthURL} from "@irati/sso/services/sso"
import tracking, {LoginAvailable, LoginProduct, LoginType} from "@irati-tracking/platform/platform"
import {AuthUrlState} from "@irati/types"
import links from "@irati/utils/links"
import Flex from "@irati/ui/src/Flex"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import session from "@irati/auth/services/session"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import {perkboxLogo} from "@irati/identity/helpers/defaultTenant"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"

import LoginBlock from "../../../components/LoginBlock"
import ForgotButton from "../styled/ForgotButton"
import StyledCheckbox from "../styled/StyledCheckbox"
import {REDIRECT_STORAGE_KEY} from "../../../constants/login"
import {BusinessLogo} from "../../OneTimePassword/styled"

interface Props extends InjectIntl, RouteComponentProps, WithBranding {
  id: string
  postedEmail: string
  onContinue: () => void
}

interface State {
  email: string
  password: string
  load: Promise<any> | null
  submit: Promise<any> | null
  rememberMe: boolean
  ssoError: string | null
  ssoURL: string | null
  useRedirect: boolean
  showAlert: boolean
  errorMsg: string
}

class FullLogin extends PureComponent<Props, State> {
  static defaultProps = {
    postedEmail: null
  }

  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  state: State = {
    email: localStorage.getItem("userEmail") || "",
    password: "",
    rememberMe: true,
    load: null,
    submit: null,
    ssoError: null,
    ssoURL: null,
    useRedirect: false,
    showAlert: false,
    errorMsg: ""
  }

  componentDidMount() {
    localStorage.setItem("login_type", LoginType.passwordLogin)
    localStorage.setItem("login_product", LoginProduct.login)
    localStorage.setItem("login_domain", authentication.unAuthedBusiness().subdomain)

    const {postedEmail} = this.props
    if (postedEmail) {
      this.setState({email: postedEmail})
    }

    const state: AuthUrlState = {}
    const nextPathname = sessionStorage.getItem(REDIRECT_STORAGE_KEY) || get(this.props, "location.state.nextPathname", "")
    const search = nextPathname.split("?")[1]
    if (search) {
      const obj = qs.parse(search)
      if (obj.useRedirectSso) {
        this.setState({useRedirect: true})
        const query = qs.stringify({nextPathname})
        state.url = `${links.getDomain(authentication.unAuthedBusiness().subdomain)}/welcome/login/idp?${query}`
      }
    }
    const getSsoUrl = getAuthURL(authentication.unAuthedBusiness().uuid!, undefined, state)
    const getSessionData = session.getThenRemove(["prevUrl"])

    const load = Promise.all([getSsoUrl, getSessionData])
      .then(([{url}, {prevUrl}]) => {
        this.setState({ssoURL: url!, showAlert: prevUrl})
        const showPasswordLogin = authentication.unAuthedBusiness().showPasswordLogin
        if (url && showPasswordLogin) {
          localStorage.setItem("login_available", LoginAvailable.passwordAndSSO)
        }
        if (url && !showPasswordLogin) {
          localStorage.setItem("login_type", LoginType.ssoLogin)
          localStorage.setItem("login_available", LoginAvailable.sso)
        }
        if (!url) {
          localStorage.setItem("login_available", LoginAvailable.password)
        }
        if (prevUrl && !url) {
          tracking.loginOTPError("No SSO available")
        }
        if (url && prevUrl) {
          this.singleSignOn()
        }
      })
      .catch((err) => {
        const ssoError = ssoErrorHandler(err)
        this.setState({ssoError})
      })

    this.setState({load})
  }

  handleSubmit = () => {
    const {onContinue} = this.props
    const {email, password} = this.state
    const business = authentication.unAuthedBusiness()
    tracking.loginOTPSubmit("email_password", undefined, undefined, email)
    localStorage.setItem("login_detail", "email_password password_login")

    const submit = loginService
      .login(business, email, password)
      .then(() => {
        onContinue()
      })
      .catch((err: PBError) => {
        const message = getMessage(this.props.formatMessage, getErrorMessage(err), getErrorValues(err))
        this.setState({errorMsg: message})
        tracking.loginOTPError("email_password", undefined, undefined)
        tracking.loginErrorDetails(message, undefined, undefined)
      })
    this.setState({submit})
  }

  handleChangeEmail = (email: string) => {
    this.setState({email, load: null})
    localStorage.setItem("userEmail", email || "")
  }

  handleChangePassword = (password: string) => this.setState({password, load: null})

  updateRememberMe = () => this.setState({rememberMe: !this.state.rememberMe})

  handleEnter = () =>
    Validation.validate()
      .then(this.handleSubmit)
      .catch(() => {})

  emailValidation = () => {
    const schema = Validation.string("platform.defaultLogin.fields.emailValidation.enterEmail")
    return schema.email("platform.defaultLogin.fields.emailValidation.notValid")
  }

  singleSignOn = () => {
    const {onContinue} = this.props
    this.setState({ssoError: null})
    const {useRedirect} = this.state
    tracking.loginOTPSubmit("sso", LoginType.ssoLogin, LoginProduct.login)

    localStorage.setItem("login_detail", "sso sso_login")
    localStorage.setItem("login_type", LoginType.ssoLogin)

    let submit: Promise<any> = initiateSSOLogin(this.state.ssoURL!, {useRedirect})
      .then((response) => {
        const {accessToken} = response
        submit = loginService.ssoLogin(accessToken).then(() => {
          onContinue()
        })
        this.setState({submit})
        return submit
      })
      .catch((err) => {
        const ssoError = ssoErrorHandler(err)
        tracking.loginOTPError("sso", LoginType.ssoLogin)
        tracking.loginErrorDetails(ssoError, LoginType.ssoLogin)
        this.setState({ssoError})
      })

    if (useRedirect) {
      this.setState({submit})
    }
  }

  handleOnBack = () => {
    return History.goBack()
  }

  render() {
    const {formatMessage, id: idNamespace, branding} = this.props
    const {email, password, rememberMe, submit, ssoError, load, ssoURL, showAlert, errorMsg} = this.state
    const {showSsoLogin, showPasswordLogin, loginPageSsoButtonText} = authentication.unAuthedBusiness()

    return (
      <div id={idNamespace} className="default_login-container">
        <Loading load={load}>
          <LoginBlock id={idNamespace} showBrandingInHeader={false} onBack={this.handleOnBack}>
            {!ssoURL && showAlert && (
              <Alert
                type={AlertTypes.error}
                title="platform.defaultLogin.noSSOAlert.title"
                subTitle="platform.defaultLogin.noSSOAlert.subtitle"
                onHide={() => this.setState({showAlert: false})}
              />
            )}
            {branding?.businessLogoUrl !== perkboxLogo && (
              <Flex justifyContent="center" marginTop={1} marginBottom={1}>
                <BusinessLogo src={formatMessage({id: branding?.businessLogoUrl})} />
              </Flex>
            )}
            <H2 marginBottom={3} textAlign="center">
              <FormattedMessage id="platform.defaultLogin.tenantLogin.title" />
            </H2>
            {/* include emergency fallback in case a tenant has showSsoLogin false && showPasswordLogin false */}
            {showPasswordLogin || (!showPasswordLogin && !showSsoLogin && !ssoURL) ? (
              <>
                <Form>
                  <Input
                    id={id("field-email", idNamespace)}
                    label={formatMessage({id: "platform.defaultLogin.fields.email.label"})}
                    name="email"
                    type="email"
                    value={email}
                    placeholder={formatMessage({id: "platform.defaultLogin.fields.email.placeholder"})}
                    onChange={this.handleChangeEmail}
                    onEnter={this.handleEnter}
                    validation={this.emailValidation()}
                  />
                  <Password
                    id={id("field-password", idNamespace)}
                    label={formatMessage({id: "platform.defaultLogin.fields.password.label"})}
                    value={password}
                    noValidation
                    isRequired
                    placeholder={formatMessage({id: "platform.defaultLogin.fields.password.placeholder"})}
                    onChange={this.handleChangePassword}
                    onEnter={this.handleEnter}
                  />
                  <FormGroup>
                    <Col xs={12}>
                      <ForgotButton id={id("btn-forgot", idNamespace)} to="/welcome/login/forgotten-password">
                        <FormattedMessage id="platform.defaultLogin.fields.button.createPassword" />
                      </ForgotButton>
                    </Col>
                  </FormGroup>
                  <StyledCheckbox
                    id={id("field-remember-me", idNamespace)}
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={this.updateRememberMe}>
                    <FormattedMessage id="platform.defaultLogin.fields.rememberMe.label" />
                  </StyledCheckbox>
                  <Loading id={id("submit-response", idNamespace)} load={submit} useLoader={false} />
                  <ValidationButton
                    primary={!showSsoLogin}
                    full
                    id={id("btn-submit", idNamespace)}
                    load={submit}
                    onClick={this.handleSubmit}>
                    <FormattedMessage
                      id={showSsoLogin ? "platform.defaultLogin.fields.button.loginEmail" : "platform.defaultLogin.fields.button.signIn"}
                    />
                  </ValidationButton>
                  {errorMsg && (
                    <Spacing marginTop>
                      <ErrorText>{formatMessage({id: errorMsg})}</ErrorText>
                    </Spacing>
                  )}
                </Form>
                <Flex width="100%" alignItems="flex-start" direction="column" marginTop={4}>
                  <Body small weight="bold" marginBottom>
                    <FormattedMessage id="platform.oneTimePassword.footer.moreWays" />
                  </Body>
                  {ssoURL && (
                    <>
                      <Button marginBottom tertiary id={id("btn-workOS-sso", idNamespace)} load={submit} onClick={this.singleSignOn}>
                        <FormattedMessage id="platform.defaultLogin.tenantLogin.help.otp.text3" />
                      </Button>
                      {ssoError && (
                        <Spacing>
                          <ErrorText>{formatMessage({id: ssoError})}</ErrorText>
                        </Spacing>
                      )}
                    </>
                  )}
                  <RouterLink to="/welcome/login">
                    <FormattedMessage id="platform.defaultLogin.tenantLogin.help.otp.text2" />
                  </RouterLink>
                </Flex>
              </>
            ) : (
              ssoURL && (
                <div>
                  <Button primary full id={id("btn-workOS-sso", idNamespace)} load={submit} onClick={this.singleSignOn}>
                    {loginPageSsoButtonText || <FormattedMessage id="platform.defaultLogin.tenantLogin.button.singleSignOn" />}
                  </Button>
                  {ssoError && (
                    <Spacing marginTop={2}>
                      <ErrorText>{formatMessage({id: ssoError})}</ErrorText>
                    </Spacing>
                  )}
                </div>
              )
            )}
            <Flex gap={1} alignItems="center" justifyContent="center" marginTop={6}>
              <Body small weight="light">
                <FormattedMessage id="platform.oneTimePassword.footer.text" />
              </Body>
              <Link href={formatMessage({id: "platform.oneTimePassword.footer.link"})} target="_blank">
                <Flex alignItems="center">
                  <FormattedMessage id="platform.oneTimePassword.footer.linkText" />
                  <Icon icon={IconTypes.chevronRight} size={12} marginLeft={0.5} />
                </Flex>
              </Link>
            </Flex>
          </LoginBlock>
        </Loading>
      </div>
    )
  }
}

export default injectIntl(withRouter(withBranding(FullLogin)))
